import React, { useState, useMemo } from 'react';
import { Table, Button, Card, Typography, Modal, Form, Select, Input, message, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;

// ══════════════════════════════════════════════
//  Mock 数据 — 内部账号池（所有平台账号）
//  TODO: 联调时替换为接口数据
// ══════════════════════════════════════════════

const MOCK_ALL_ACCOUNTS = [
  { id: 'user-01', name: '李客服', account: 'lifuwu' },
  { id: 'user-02', name: '王客服', account: 'wangyuan' },
  { id: 'user-03', name: '张客服', account: 'zhangwei' },
  { id: 'user-04', name: '赵客服', account: 'zhaoyun' },
  { id: 'user-05', name: '陈客服', account: 'chenli' },
  { id: 'user-06', name: '刘运维', account: 'liuyunwei' },
  { id: 'user-07', name: '周运营', account: 'zhouyun' },
  { id: 'user-08', name: '吴测试', account: 'wuceshi' },
];

// ══════════════════════════════════════════════
//  Mock 数据 — 已添加的客服账号
//  TODO: 联调时替换为接口数据
// ══════════════════════════════════════════════

const MOCK_AGENTS = [
  { key: '1', id: 'user-01', name: '李客服', account: 'lifuwu', remark: '负责华南区域，擅长SDK对接' },
  { key: '2', id: 'user-02', name: '王客服', account: 'wangyuan', remark: '负责华东区域' },
  { key: '3', id: 'user-03', name: '张客服', account: 'zhangwei', remark: '处理紧急工单' },
];

// ══════════════════════════════════════════════
//  主组件
// ══════════════════════════════════════════════

function AgentList() {
  const [agents, setAgents] = useState(MOCK_AGENTS);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [remark, setRemark] = useState('');
  const [form] = Form.useForm();

  // 已添加的账号 ID 集合
  const addedIds = useMemo(() => new Set(agents.map(a => a.id)), [agents]);

  // 可选账号：内部账号池中排除已添加的
  const availableAccounts = useMemo(
    () => MOCK_ALL_ACCOUNTS.filter(a => !addedIds.has(a.id)),
    [addedIds]
  );

  const handleAdd = () => {
    setSelectedAccount(null);
    setRemark('');
    form.resetFields();
    setModalVisible(true);
  };

  const handleAddConfirm = () => {
    if (!selectedAccount) {
      message.warning('请选择账号');
      return;
    }
    const account = MOCK_ALL_ACCOUNTS.find(a => a.id === selectedAccount);
    if (!account) return;

    const newAgent = {
      key: Date.now().toString(),
      id: account.id,
      name: account.name,
      account: account.account,
      remark: remark.trim(),
    };
    setAgents(prev => [...prev, newAgent]);
    message.success('已添加');
    setModalVisible(false);
  };

  const handleRemove = (record) => {
    setAgents(prev => prev.filter(a => a.key !== record.key));
    message.success('已移除');
  };

  const columns = [
    {
      title: '姓名', dataIndex: 'name', key: 'name', width: 150,
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: '账号', dataIndex: 'account', key: 'account', width: 150,
      render: (text) => <Text code style={{ color: '#666' }}>{text}</Text>,
    },
    {
      title: '备注', dataIndex: 'remark', key: 'remark', ellipsis: true,
      render: (text) => text || <span style={{ color: '#ccc' }}>-</span>,
    },
    {
      title: '操作', key: 'action', width: 100, fixed: 'right',
      render: (_, record) => (
        <Popconfirm title="确认移除该客服？" onConfirm={() => handleRemove(record)}>
          <Button type="link" size="small" danger icon={<DeleteOutlined />}>移除</Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4} style={{ margin: 0 }}>客服账号</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>添加客服</Button>
      </div>

      <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
        <Table
          columns={columns}
          dataSource={agents}
          rowKey="key"
          pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录`, defaultPageSize: 20 }}
          locale={{
            emptyText: (
              <div style={{ padding: 40 }}>
                <Text type="secondary">暂无客服账号，点击上方「添加客服」添加</Text>
              </div>
            ),
          }}
          scroll={{ x: 600 }}
        />
      </Card>

      {/* 添加弹窗 */}
      <Modal
        title="添加客服"
        open={modalVisible}
        onOk={handleAddConfirm}
        onCancel={() => setModalVisible(false)}
        okText="确认添加"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item label="选择账号" required>
            <Select
              placeholder="请选择内部账号"
              value={selectedAccount}
              onChange={setSelectedAccount}
              showSearch
              optionFilterProp="children"
              style={{ width: '100%' }}
              notFoundContent={availableAccounts.length === 0 ? '所有账号已添加' : '无匹配账号'}
            >
              {availableAccounts.map(acc => (
                <Option key={acc.id} value={acc.id}>
                  {acc.name}  {acc.account}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="备注">
            <Input.TextArea
              rows={3}
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder="选填，如：负责华南区域、擅长技术支持"
              maxLength={200}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default AgentList;
